import { useEffect, useRef, type ReactNode } from 'react'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Open traps, newest last. Only the newest handles Tab, so a dialog opened
// over another keeps focus in the top one.
const open: HTMLElement[] = []

/**
 * Keyboard focus for a dialog, for as long as it is mounted.
 *
 * On mount, focus moves into it -- unless something inside already took it
 * (an `autoFocus`). Tab and Shift+Tab wrap around inside it rather than
 * wandering into the page behind. On unmount, focus goes back to whatever had
 * it before, so closing a dialog leaves a keyboard user where they were.
 *
 * Renders `display: contents`, so it does not change the dialog's layout.
 */
export function FocusTrap({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  // Read while rendering, before an autoFocus inside the dialog moves focus
  // in during the commit -- by the effect it would name the dialog's own button.
  const openerRef = useRef(document.activeElement as HTMLElement | null)

  useEffect(() => {
    const box = ref.current
    if (!box) return
    const opener = openerRef.current
    open.push(box)
    const focusables = () =>
      [...box.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.getClientRects().length > 0)

    if (!box.contains(document.activeElement)) focusables()[0]?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || open[open.length - 1] !== box) return
      const items = focusables()
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      if (e.shiftKey && (active === first || !box.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (active === last || !box.contains(active))) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      open.splice(open.indexOf(box), 1)
      if (opener?.isConnected) opener.focus()
    }
  }, [])

  return <div ref={ref} className="contents">{children}</div>
}
